// Konversationsansicht: Werkzeugleiste (Icons + Tooltips), Nachrichten (neueste oben, ältere eingeklappt),
// Aussortiert-Kasten bei Info/Junk, Antwort-Editor.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import {
  TEAM,
  isUserKey,
  type Draft,
  type Message,
  type ThreadDetail,
  type ThreadListItem,
} from '@mail/shared';
import { api, ApiError } from '../lib/api.ts';
import { displayName, fmtDateTime, initials } from '../lib/format.ts';
import {
  getState,
  nextAfter,
  onThreadsChanged,
  patchThread,
  removeThread,
  select,
  toggleCrm,
  useStore,
} from '../lib/store.ts';
import { naechsterTreffer } from '../lib/suche.ts';
import { IC } from './Icons.tsx';
import { MessageBody, type MailtoHandler } from './MessageBody.tsx';
import { ReplyBox } from './ReplyBox.tsx';
import { toast } from './Toast.tsx';
import { Notizen } from './Notizen.tsx';
import { onEvent } from '../lib/store.ts';
import { MOBIL, useMedia } from '../lib/media.ts';
import { oeffneVorschau, vorschauArt } from '../lib/vorschau.ts';

function MessageCard({
  m,
  open,
  color,
  onToggle,
  onMailto,
}: {
  m: Message;
  open: boolean;
  color: string;
  onToggle: () => void;
  onMailto: MailtoHandler;
}) {
  const from = m.from;
  const own = m.direction === 'out';
  return (
    <article className={`msg ${open ? '' : 'coll'}`} data-id={m.id}>
      <div
        className="msg-h"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onToggle()}
      >
        <div
          className="av"
          style={{ background: `var(--mb-${color})`, width: 30, height: 30, fontSize: 11 }}
        >
          {own ? 'M' : initials(from)}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="nm">
            {displayName(from)}{' '}
            {!own && (
              <button
                className="lnk muted"
                style={{ fontWeight: 400 }}
                title={`Neue Mail an ${from.address}`}
                onClick={(e) => {
                  e.stopPropagation();
                  void onMailto(from);
                }}
              >
                &lt;{from.address}&gt;
              </button>
            )}
          </div>
          {open ? (
            <div className="to">
              An: {m.to.map(displayName).join(', ')}
              {m.cc.length ? ` · Cc: ${m.cc.map(displayName).join(', ')}` : ''}
            </div>
          ) : (
            <div className="prev">{m.snippet}</div>
          )}
        </div>
        <span className="dt">{fmtDateTime(m.date)}</span>
      </div>
      {open && <MessageBody m={m} onMailto={onMailto} />}
    </article>
  );
}

// Hört-Hin!-Postfächer: Bob ohne CRM, mit der Wissensbasis (Ausbildung, minimusiker+, Medienzentren)
const HH_CHIPS = [
  'Antworte auf alle Fragen',
  'Nenne Preise und nächste Schritte',
  'Kürzer',
  'Freundlicher',
];
const CHIPS = [
  'Antworte auf alle Fragen',
  'kürzer',
  'bedank dich und sag, wir melden uns bis Freitag',
];

export function Conversation({ id }: { id: number }) {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const { mailboxes, me, crmOpen } = useStore((s) => ({
    mailboxes: s.mailboxes,
    me: s.me,
    crmOpen: s.crmOpen,
  }));
  const [data, setData] = useState<ThreadDetail | null>(null);
  const [open, setOpen] = useState<Set<number>>(new Set());
  const [draft, setDraft] = useState<Draft | null>(null);
  const [menu, setMenu] = useState<'' | 'assign' | 'later' | 'wait' | 'task' | 'more'>('');
  const [parallel, setParallel] = useState<ThreadListItem[]>([]);
  const [scheduled, setScheduled] = useState<
    Array<{ commandId: number; draftId: string; at: string; by: string | null }>
  >([]);
  const [manual, setManual] = useState(false);
  const [andere, setAndere] = useState<string[]>([]); // wer schreibt gerade (Kollisionsschutz)
  const seenRef = useRef<number | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  // Handy: Antwortfeld im Fluss unter den Nachrichten; Desktop: unten angedockt, die letzte Mail bleibt darüber sichtbar
  const mobil = useMedia(MOBIL);
  useEffect(() => {
    void api
      .presenceLesen(id)
      .then((r) => setAndere(r.andere))
      .catch(() => setAndere([]));
    return onEvent((ev) => {
      if (ev.kind !== 'presence' || ev.threadId !== id || ev.user === me?.key) return;
      setAndere((l) =>
        ev.editing ? [...new Set([...l, ev.user])] : l.filter((u) => u !== ev.user),
      );
    });
  }, [id, me?.key]);
  // Eigener offener Entwurf zu diesem Thread (weggeklickt, anderer Thread, Neuladen): wieder aufmachen.
  // Verworfene und gesendete Entwürfe nicht noch einmal (bis die Daten neu geladen sind).
  const entwurfErledigt = useRef<Set<string>>(new Set());
  useEffect(() => {
    const e = data?.meinEntwurf;
    if (!e || draft || entwurfErledigt.current.has(e.draftId)) return;
    void api
      .draft(e.draftId)
      .then((d) => {
        if (d.status === 'draft') setDraft(d);
      })
      .catch(() => undefined);
  }, [data, draft]);
  const vorschlagOeffnen = async () => {
    const v = data?.vorschlag;
    if (!v) return;
    try {
      const did = v.user === me?.key ? v.draftId : (await api.uebernehmen(v.draftId)).draftId;
      setDraft(await api.draft(did));
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Vorschlag nicht verfügbar');
    }
  };

  const load = useCallback(async () => {
    try {
      const d = await api.thread(id);
      setData(d);
      void api
        .parallel(id)
        .then((r) => setParallel(r.threads))
        .catch(() => setParallel([]));
      void api
        .scheduled(id)
        .then((r) => setScheduled(r.scheduled))
        .catch(() => setScheduled([]));
      setOpen((o) => (o.size ? o : new Set([d.messages[0]?.id ?? 0])));
      if (seenRef.current !== id && d.messages.some((m) => !m.seen && m.direction === 'in')) {
        seenRef.current = id;
        void api.seen(id, true);
        patchThread(id, { unreadCount: 0 });
      }
    } catch {
      setData(null);
    }
  }, [id]);
  useEffect(() => {
    setData(null);
    setDraft(null);
    setOpen(new Set());
    setManual(false);
    select(id);
    void load();
    // Von Bob (⌘K) angelegter Entwurf: ?draft=<id>
    const did = params.get('draft');
    if (did) {
      void api.draft(did).then((d) => {
        setDraft(d);
        setParams({}, { replace: true });
      });
    }
    return () => select(null);
  }, [id, load]);
  useEffect(
    () => onThreadsChanged((ids) => (!ids.length || ids.includes(id)) && void load()),
    [id, load],
  );

  const t = data?.thread;
  const mb = useMemo(() => mailboxes.find((m) => m.id === t?.mailboxId), [mailboxes, t]);
  const lastIn = data?.messages.find((m) => m.direction === 'in') ?? data?.messages[0];

  // Aus der Suche heraus geöffnet (D-071): Links bleiben in der Suche, „nächste“ ist der nächste Treffer
  const inSuche = useLocation().pathname.startsWith('/search');
  const zu = (tid: number) => (inSuche ? `/search/t/${tid}` : `/t/${tid}`);
  const heim = inSuche ? '/search' : '/';
  const goNext = () => {
    const n = inSuche ? naechsterTreffer(id) : nextAfter(id);
    removeThread(id);
    void nav(n ? zu(n) : heim);
  };
  const done = async () => {
    await api.done(id);
    goNext();
    toast('Erledigt ✓', { undo: () => api.reopen(id).then(() => void nav(zu(id))) });
  };
  // Aufgabe / Erinnerung aus der Mail (2.D): Flow-Aufgabe (Team) oder CRM-Erinnerung (Schulbezug, über Bob)
  const tagIn = (n: number) => {
    const d = new Date();
    d.setDate(d.getDate() + n);
    if (n > 0 && d.getDay() === 0) d.setDate(d.getDate() + 1);
    if (n > 0 && d.getDay() === 6) d.setDate(d.getDate() + 2);
    return d;
  };
  const flowAufgabe = async (tage: number | null) => {
    setMenu('');
    const title = prompt('Flow-Aufgabe', t?.subject || 'Aufgabe aus Mail');
    if (!title) return;
    try {
      const due = tage === null ? null : new Date(tagIn(tage).setHours(9, 0, 0, 0)).toISOString();
      const r = await api.flowAufgabe(id, { title, dueDate: due });
      toast(`Flow-Aufgabe ${r.task.id} angelegt`);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Flow nicht erreichbar');
    }
  };
  const crmErinnerung = async (tage: number) => {
    setMenu('');
    const titel = prompt('CRM-Erinnerung', `Nachhaken: ${t?.subject || ''}`);
    if (!titel) return;
    try {
      const d = tagIn(tage);
      const faelligAm = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      await api.crmErinnerung(id, { titel, faelligAm });
      toast(`CRM-Erinnerung für ${faelligAm} gesetzt`);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'CRM nicht erreichbar');
    }
  };
  const later = async (preset: 'today16' | 'tomorrow' | 'monday' | 'nextweek', label: string) => {
    setMenu('');
    await api.later(id, preset);
    goNext();
    toast(`Zurückgestellt: ${label}`, {
      undo: () => api.reopen(id).then(() => void nav(zu(id))),
    });
  };
  // Neue Mail an eine Adresse (Klick auf mailto: im Mailtext oder auf die Absenderadresse) – in der App,
  // aus dem Postfach dieses Threads, nicht in Mail.app
  const neueMail: MailtoHandler = async (to, subject) => {
    if (!t) return;
    try {
      const d = await api.newDraft({
        mailboxId: t.mailboxId,
        to: [{ address: to.address, ...(to.name ? { name: to.name } : {}) }],
        ...(subject ? { subject } : {}),
      });
      void nav(`/compose/${d.id}`);
    } catch {
      toast('Neue Mail konnte nicht angelegt werden');
    }
  };
  const reply = async (mode: 'reply' | 'replyAll' | 'forward') => {
    if (!lastIn) return;
    const d = await api.replyDraft(lastIn.id, mode);
    setDraft(d);
    // Die neueste Mail (oben) soll beim Antworten sichtbar sein
    bodyRef.current?.scrollTo({ top: 0 });
    setTimeout(() => document.getElementById('bobIn')?.focus(), 50);
  };

  // Tastenkürzel im Thread: r antworten, a allen, f weiterleiten, e erledigt, u ungelesen
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (
        e.metaKey ||
        e.ctrlKey ||
        e.altKey ||
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) ||
        (e.target as HTMLElement)?.isContentEditable
      )
        return;
      if (e.key === 'r') void reply('reply');
      if (e.key === 'a') void reply('replyAll');
      if (e.key === 'f') void reply('forward');
      if (e.key === 'e') void done();
      if (e.key === 'u') void api.seen(id, false).then(() => patchThread(id, { unreadCount: 1 }));
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [id, lastIn]);

  if (!data || !t) return <section className="conv" aria-label="Konversation" />;
  const assigned = t.assignedTo && isUserKey(t.assignedTo) ? TEAM[t.assignedTo] : null;
  const sorted = t.category !== 'wichtig' && !manual;
  // Alle Anhänge des Threads (neueste zuerst) für die Leiste unter dem Kopf
  const anhaenge = data.messages.flatMap((m) => m.attachments.filter((a) => !a.inline));
  const replyBox = draft ? (
    <ReplyBox
      key={draft.id}
      draft={draft}
      mailboxes={mailboxes.filter((m) => m.visibility === 'shared' || m.owner === me?.key)}
      bobEnabled={!!mb?.bobEnabled && !manual}
      // Zuerst die Anliegen der letzten eingegangenen Mail (beim Sync erkannt), dann die festen Chips
      chips={
        manual
          ? []
          : [...(lastIn?.anliegen ?? []), ...(mb?.bobEnabled ? CHIPS : HH_CHIPS)].slice(0, 7)
      }
      leadChip={!!mb?.bobEnabled && !manual && !t.crmInstitutionId}
      onDiscard={() => {
        entwurfErledigt.current.add(draft.id);
        void api.deleteDraft(draft.id).then(() => setDraft(null));
      }}
      onSent={({ thenDone, undo, sendAt }) => {
        entwurfErledigt.current.add(draft.id);
        setDraft(null);
        if (thenDone) goNext();
        toast(
          sendAt
            ? `Geplant: ${fmtDateTime(sendAt)}${thenDone ? ' · erledigt' : ''}`
            : thenDone
              ? 'Gesendet & erledigt'
              : 'Gesendet',
          {
            undo: () =>
              undo().then(() => {
                toast('Senden abgebrochen – der Entwurf ist noch da.');
                void nav(zu(id));
              }),
          },
        );
      }}
    />
  ) : null;

  return (
    <section className="conv" aria-label="Konversation">
      <div className="tb">
        <button className="back" onClick={() => void nav(heim)}>
          {IC.back} Zurück
        </button>
        <button
          className="ib"
          data-tip="Antworten (R)"
          aria-label="Antworten"
          onClick={() => void reply('reply')}
          id="replyBtn"
        >
          {IC.reply}
        </button>
        <button
          className="ib mob-hide"
          data-tip="Allen antworten (A)"
          aria-label="Allen antworten"
          onClick={() => void reply('replyAll')}
        >
          {IC.replyAll}
        </button>
        <button
          className="ib mob-hide"
          data-tip="Weiterleiten (F)"
          aria-label="Weiterleiten"
          onClick={() => void reply('forward')}
        >
          {IC.fwd}
        </button>
        <i className="tb-sep mob-hide" />
        {/* Zuweisen: eine Person je Knopf, noch einmal klicken hebt die Zuweisung auf */}
        <span className="tb-assign mob-hide" role="group" aria-label="Zuweisen">
          {(Object.keys(TEAM) as Array<keyof typeof TEAM>).map((k) => {
            const aktiv = t.assignedTo === k;
            const wer = k === me?.key ? 'mich' : TEAM[k].name;
            return (
              <button
                key={k}
                className={`ib ${aktiv ? 'on' : ''}`}
                data-tip={aktiv ? `Bei ${TEAM[k].name} – Zuweisung aufheben` : `An ${wer} zuweisen`}
                aria-label={aktiv ? `Zuweisung an ${TEAM[k].name} aufheben` : `An ${wer} zuweisen`}
                aria-pressed={aktiv}
                onClick={() => {
                  const ziel = aktiv ? null : k;
                  void api.assign(id, ziel).then(() => patchThread(id, { assignedTo: ziel }));
                }}
              >
                <span
                  className={`mini-av ${aktiv ? '' : 'dim'}`}
                  style={{ background: TEAM[k].color }}
                >
                  {TEAM[k].name[0]}
                </span>
              </button>
            );
          })}
        </span>
        <i className="tb-sep mob-hide" />
        <div className="menu-wrap">
          <button
            className={`ib ${t.pinned || t.status === 'later' || t.waitUntil ? 'on' : ''}`}
            data-tip="Mehr"
            aria-label="Weitere Aktionen"
            aria-expanded={menu !== ''}
            onClick={() => setMenu(menu ? '' : 'more')}
          >
            ⋯
          </button>
          {menu === 'more' && (
            <div className="menu" onMouseLeave={() => setMenu('')}>
              <button
                onClick={() => {
                  setMenu('');
                  void api.pin(id, !t.pinned).then(() => patchThread(id, { pinned: !t.pinned }));
                }}
              >
                📌 {t.pinned ? 'Lösen' : 'Anheften'}
              </button>
              <button onClick={() => setMenu('later')}>
                ⏰ Später{t.status === 'later' ? ' (zurückgestellt)' : ''} …
              </button>
              <button onClick={() => setMenu('wait')}>
                ⌛ {t.waitUntil ? `Wartet auf Antwort (${t.waitDays} Tage)` : 'Auf Antwort warten'}{' '}
                …
              </button>
              <button onClick={() => setMenu('task')}>☑ Aufgabe / Erinnerung …</button>
              <button
                onClick={() => {
                  setMenu('');
                  void api.seen(id, false).then(() => patchThread(id, { unreadCount: 1 }));
                }}
              >
                ✉ Als ungelesen (U)
              </button>
              <button
                className="mob-only"
                onClick={() => {
                  setMenu('');
                  void reply('replyAll');
                }}
              >
                Allen antworten
              </button>
              <button
                className="mob-only"
                onClick={() => {
                  setMenu('');
                  void reply('forward');
                }}
              >
                Weiterleiten
              </button>
              <button
                className="mob-only"
                onClick={() => {
                  setMenu('');
                  const ziel = t.assignedTo === me?.key ? null : (me?.key ?? null);
                  void api.assign(id, ziel).then(() => patchThread(id, { assignedTo: ziel }));
                }}
              >
                👤 {t.assignedTo === me?.key ? 'Zuweisung aufheben' : 'Mir zuweisen'}
              </button>
              <button
                onClick={() => {
                  setMenu('');
                  toggleCrm();
                }}
              >
                CRM-Leiste {crmOpen ? 'ausblenden' : 'einblenden'}
              </button>
            </div>
          )}
          {menu === 'later' && (
            <div className="menu" onMouseLeave={() => setMenu('')}>
              <div className="menu-h">Später vorlegen</div>
              <button onClick={() => void later('today16', 'heute 16:00')}>⏰ Heute 16:00</button>
              <button onClick={() => void later('tomorrow', 'morgen früh')}>⏰ Morgen früh</button>
              <button onClick={() => void later('monday', 'Montag früh')}>⏰ Montag früh</button>
              <button onClick={() => void later('nextweek', 'in einer Woche')}>
                ⏰ In einer Woche
              </button>
              <button className="muted" onClick={() => setMenu('more')}>
                ← zurück
              </button>
            </div>
          )}
          {menu === 'wait' && (
            <div className="menu" onMouseLeave={() => setMenu('')}>
              <div className="menu-h">Auf Antwort warten</div>
              {[3, 5, 10, 14].map((d) => (
                <button
                  key={d}
                  onClick={() => void api.wait(id, d).then(() => (setMenu(''), load()))}
                >
                  ⌛ nach {d} Tagen nachfassen{t.waitDays === d && t.waitUntil ? ' ✓' : ''}
                </button>
              ))}
              <button onClick={() => void api.wait(id, null).then(() => (setMenu(''), load()))}>
                — nicht warten
              </button>
              <button className="muted" onClick={() => setMenu('more')}>
                ← zurück
              </button>
            </div>
          )}
          {menu === 'task' && (
            <div className="menu" onMouseLeave={() => setMenu('')}>
              <div className="menu-h">Flow-Aufgabe (Team)</div>
              <button onClick={() => void flowAufgabe(0)}>☑ heute</button>
              <button onClick={() => void flowAufgabe(1)}>☑ morgen</button>
              <button onClick={() => void flowAufgabe(7)}>☑ in einer Woche</button>
              <button onClick={() => void flowAufgabe(null)}>☑ ohne Datum</button>
              <div className="menu-h">
                CRM-Erinnerung (Schule){!t.crmInstitutionId ? ' – ohne Einrichtung' : ''}
              </div>
              <button onClick={() => void crmErinnerung(1)}>⏰ morgen</button>
              <button onClick={() => void crmErinnerung(3)}>⏰ in 3 Tagen</button>
              <button onClick={() => void crmErinnerung(7)}>⏰ in einer Woche</button>
              <button className="muted" onClick={() => setMenu('more')}>
                ← zurück
              </button>
            </div>
          )}
        </div>
        <span className="sp" />
        {t.status === 'done' ? (
          <button
            className="done-btn on"
            title="Wieder öffnen"
            onClick={() => void api.reopen(id).then(() => patchThread(id, { status: 'open' }))}
          >
            {IC.done} Wieder öffnen
          </button>
        ) : (
          <button
            className="done-btn"
            title="Erledigt (E)"
            onClick={() => void done()}
            id="doneBtn"
          >
            {IC.done} Erledigt
          </button>
        )}
        <button
          className={`ib last mob-hide ${crmOpen ? 'on' : ''}`}
          data-tip="CRM-Leiste"
          aria-label="CRM-Leiste ein/aus"
          onClick={toggleCrm}
        >
          {IC.panel}
        </button>
      </div>
      <div className="conv-body" ref={bodyRef}>
        <div className="conv-head">
          <h2 className="conv-title">{t.subject || '(kein Betreff)'}</h2>
          <div className="conv-meta">
            {mb && <span className={`dot c-${mb.color}`} />}
            {mb?.address}
            <span>·</span>
            <span>{t.messageCount === 1 ? '1 Nachricht' : `${t.messageCount} Nachrichten`}</span>
            {assigned && <span className="pill mut">bei {assigned.name}</span>}
            {t.status === 'done' && (
              <span className="pill ok">
                erledigt{t.statusBy && isUserKey(t.statusBy) ? ` von ${TEAM[t.statusBy].name}` : ''}
              </span>
            )}
            {t.status === 'later' && <span className="pill warn">zurückgestellt</span>}
            {t.crmInstitutionName && (
              <span className="pill einrichtung">{t.crmInstitutionName}</span>
            )}
            {(data?.regeln ?? []).slice(0, 2).map((r) => (
              <span
                key={r.id}
                className={`pill ${r.ergebnis === 'ausgeführt' ? 'mut' : 'warn'}`}
                title={r.aktionen
                  .map((a) => `${a.ok ? '✓' : '✗'} ${a.typ}${a.info ? ' – ' + a.info : ''}`)
                  .join('\n')}
              >
                ⚙ Regel „{r.ruleName}“: {r.ergebnis}
                {r.aktionen.length
                  ? ' · ' +
                    r.aktionen
                      .filter((a) => a.ok)
                      .map((a) => a.typ)
                      .join(', ')
                  : ''}
              </span>
            ))}
          </div>
        </div>
        {!sorted && !draft && (
          <div className="conv-actions">
            <button className="btn sm" onClick={() => void reply('reply')} title="Antworten (R)">
              {IC.reply} Antworten
            </button>
            <button
              className="btn sm"
              onClick={() => void reply('replyAll')}
              title="Allen antworten (A)"
            >
              {IC.replyAll} Allen
            </button>
            <button
              className="btn sm"
              onClick={() => void reply('forward')}
              title="Weiterleiten (F)"
            >
              {IC.fwd} Weiterleiten
            </button>
            {mb?.bobEnabled && t.category === 'wichtig' && (
              <button
                className="btn sm bob"
                onClick={() => void reply('reply')}
                title="Antwort mit Bob entwerfen"
              >
                ✦ Bob fragen
              </button>
            )}
            {anhaenge.length > 0 && (
              <div className="conv-atts" aria-label="Anhänge im Thread">
                {anhaenge.slice(0, 6).map((a) => (
                  <a
                    key={a.id}
                    className="att"
                    href={`/api/attachments/${a.id}`}
                    target="_blank"
                    rel="noopener"
                    title={`${a.filename} (${a.mime})`}
                    onClick={(e) => {
                      if (!vorschauArt(a)) return;
                      e.preventDefault();
                      oeffneVorschau(a);
                    }}
                  >
                    📎 {a.filename}
                  </a>
                ))}
                {anhaenge.length > 6 && (
                  <span className="muted" style={{ fontSize: 12 }}>
                    +{anhaenge.length - 6}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
        {(data?.versandFehler ?? []).map((f) => (
          <div className="callout warn" key={f.commandId} role="alert">
            ⚠ Senden fehlgeschlagen{f.an ? ` an ${f.an}` : ''}
            {f.subject ? ` („${f.subject}“)` : ''}: {f.error ?? 'unbekannter Fehler'}{' '}
            <button
              className="btn sm"
              onClick={() =>
                void api
                  .commandRetry(f.commandId)
                  .then(() => toast('Wird erneut gesendet …'))
                  .catch((e) =>
                    toast(e instanceof ApiError ? e.message : 'Erneut senden nicht möglich'),
                  )
              }
            >
              Erneut senden
            </button>
          </div>
        ))}
        {andere.length > 0 && (
          <div className="callout warn">
            ✍ {andere.map((u) => (isUserKey(u) ? TEAM[u].name : u)).join(' und ')}{' '}
            {andere.length > 1 ? 'schreiben' : 'schreibt'} gerade eine Antwort in diesem Thread.
          </div>
        )}
        {data?.vorschlag && !draft && t.status !== 'done' && (
          <div
            className="callout bobsum"
            style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
          >
            <span>
              ✦ <b>Vorschlag von Bob</b> liegt bereit
              {data.vorschlag.user !== me?.key && isUserKey(data.vorschlag.user)
                ? ` (für ${TEAM[data.vorschlag.user].name})`
                : ''}
              {data.vorschlag.anweisung
                ? ` – ${data.vorschlag.anweisung.replace(/^Hintergrund-Vorschlag: /, '')}`
                : ''}
            </span>
            <button className="btn sm pri" onClick={() => void vorschlagOeffnen()}>
              Vorschlag öffnen
            </button>
          </div>
        )}
        <Notizen threadId={id} />
        {parallel.length > 0 && (
          <div className="callout info" style={{ background: 'var(--accent-soft)' }}>
            Weitere offene Mail von {t.crmInstitutionName ?? 'derselben Einrichtung'}:{' '}
            {parallel.map((p, i) => (
              <span key={p.id}>
                {i > 0 && ' · '}
                <button className="linkbtn" onClick={() => void nav(zu(p.id))}>
                  {p.subject || '(kein Betreff)'}
                </button>{' '}
                ({mailboxes.find((m) => m.id === p.mailboxId)?.address.split('@')[0]}
                {p.assignedTo && isUserKey(p.assignedTo) ? `, bei ${TEAM[p.assignedTo].name}` : ''})
              </span>
            ))}
          </div>
        )}
        {scheduled.map((sch) => (
          <div
            key={sch.commandId}
            className="callout warn"
            style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
          >
            ⏱ Geplanter Versand: {fmtDateTime(sch.at)}
            {sch.by && isUserKey(sch.by) ? ` (${TEAM[sch.by].name})` : ''}
            <button
              className="btn sm"
              onClick={() =>
                void api
                  .undo(sch.draftId)
                  .then(() => (toast('Versand abgebrochen – der Entwurf ist noch da.'), load()))
              }
            >
              Abbrechen
            </button>
          </div>
        ))}
        {t.nachfassDraftId && !draft && (
          <div
            className="callout bobsum"
            style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
          >
            <b>✦ Keine Antwort seit {t.waitDays} Tagen</b> – Bob hat einen Nachfass-Entwurf
            vorbereitet.
            <button
              className="btn sm"
              onClick={() => void api.draft(t.nachfassDraftId!).then(setDraft)}
            >
              Entwurf öffnen
            </button>
          </div>
        )}
        {data.messages.map((m, i) => (
          <MessageCard
            key={m.id}
            m={m}
            color={mb?.color ?? 'green'}
            onMailto={neueMail}
            open={open.has(m.id) || (i === 0 && !open.size)}
            onToggle={() =>
              setOpen((o) => {
                const n = new Set(o);
                if (n.has(m.id)) n.delete(m.id);
                else n.add(m.id);
                return n;
              })
            }
          />
        ))}
        {sorted ? (
          <section
            className={`sorted-box ${t.category === 'junk' ? 'junk' : ''}`}
            aria-label="Aussortiert"
          >
            <div className="sb-h">
              {t.category === 'junk' ? '🚫 Als Junk aussortiert' : '🔔 Als Info-Mail aussortiert'}
            </div>
            <p>{t.categoryReason || 'Automatisch eingeordnet.'}</p>
            <p className="sb-s">Deshalb gibt es hier keinen Bob-Entwurf.</p>
            <div className="sb-f">
              <button
                className="btn pri"
                onClick={() => void api.category(id, 'wichtig', 'sender').then(() => load())}
              >
                Doch in den Posteingang
              </button>
              {t.category === 'junk' ? (
                <button
                  className="btn"
                  onClick={() =>
                    void api
                      .category(id, 'junk', 'sender', true)
                      .then(() => (toast('Absender blockiert'), goNext()))
                  }
                >
                  Absender blockieren
                </button>
              ) : (
                <>
                  <button
                    className="btn"
                    onClick={() =>
                      void api
                        .category(id, 'info', 'sender')
                        .then(() => toast('Absender wird künftig aussortiert'))
                    }
                  >
                    Immer aussortieren
                  </button>
                  <button className="btn ghost" onClick={() => setManual(true)}>
                    Trotzdem antworten
                  </button>
                </>
              )}
            </div>
          </section>
        ) : null}
        {mobil && replyBox}
      </div>
      {!mobil && replyBox}
    </section>
  );
}

export const selectedId = () => getState().selected;
